import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import {
  LPR_PROVIDER_OPTIONS,
  type LprProviderKind,
  type LprSettings,
} from "@/lib/barrierLprSettings";
import { Button } from "@/components/ui/Button";
import { FormHint } from "@/pages/stammdaten/schnittstellen/FormHint";

interface TenantSettings {
  lpr: LprSettings;
}

interface LprUpdateResponse {
  settings: LprSettings;
  api_key: string | null;
}

interface LprIntegrationPanelProps {
  canWrite: boolean;
}

export function LprIntegrationPanel({ canWrite }: LprIntegrationPanelProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revealedKey, setRevealedKey] = useState<string | null>(null);

  const [enabled, setEnabled] = useState(false);
  const [provider, setProvider] = useState<LprProviderKind>("generic");
  const [autoOpenBarrier, setAutoOpenBarrier] = useState(true);
  const [matchCheckedIn, setMatchCheckedIn] = useState(true);
  const [matchConfirmed, setMatchConfirmed] = useState(true);
  const [apiKeyConfigured, setApiKeyConfigured] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [inboundPath, setInboundPath] = useState("/api/v1/public/lpr/events");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const settings = await api.get<TenantSettings>("/api/v1/settings");
      const lpr = settings.lpr;
      setEnabled(lpr.enabled);
      setProvider(lpr.provider);
      setAutoOpenBarrier(lpr.auto_open_barrier);
      setMatchCheckedIn(lpr.match_checked_in);
      setMatchConfirmed(lpr.match_confirmed);
      setApiKeyConfigured(lpr.api_key_configured);
      setConfigured(lpr.configured);
      setInboundPath(lpr.inbound_path);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Einstellungen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const selected = LPR_PROVIDER_OPTIONS.find((p) => p.value === provider);

  async function save(options?: { rotate?: boolean; clear?: boolean }) {
    if (!canWrite) return;
    setSaving(true);
    setSuccess(false);
    setError(null);
    try {
      const body = {
        enabled,
        provider,
        auto_open_barrier: autoOpenBarrier,
        match_checked_in: matchCheckedIn,
        match_confirmed: matchConfirmed,
        rotate_api_key: Boolean(options?.rotate),
        clear_api_key: Boolean(options?.clear),
      };
      const updated = await api.patch<LprUpdateResponse>("/api/v1/settings/lpr", body);
      setApiKeyConfigured(updated.settings.api_key_configured);
      setConfigured(updated.settings.configured);
      setInboundPath(updated.settings.inbound_path);
      setRevealedKey(updated.api_key);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "LPR konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <p className="text-sm text-label-secondary">Laden…</p>;
  }

  return (
    <div className="space-y-5">
      <FormHint>
        Kameras und Kennzeichenerkennungsanlagen senden erkannte Kennzeichen an CampDesk. Bei Treffer
        mit einer Buchung kann optional die konfigurierte Schranke geöffnet werden.
      </FormHint>

      <label className="flex items-center gap-3 rounded-xl border border-separator bg-fill-quaternary p-4 text-sm">
        <input
          type="checkbox"
          className="rounded border-separator text-accent focus:ring-accent"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          disabled={!canWrite}
        />
        <span className="font-medium text-label">Kennzeichenerkennung aktivieren</span>
      </label>

      {enabled && configured ? (
        <p className="rounded-xl border border-status-green/30 bg-status-green/10 px-4 py-3 text-sm text-status-green">
          Empfangsbereit ({selected?.label ?? provider}).
        </p>
      ) : null}

      {error ? (
        <p className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-700">
          {error}
        </p>
      ) : null}
      {success ? (
        <p className="rounded-xl border border-status-green/30 bg-status-green/10 px-4 py-3 text-sm text-status-green">
          Gespeichert.
        </p>
      ) : null}

      {revealedKey ? (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm">
          <p className="font-medium text-amber-900 dark:text-amber-100">
            Neuer API-Key (nur einmal sichtbar):
          </p>
          <code className="mt-2 block break-all text-xs">{revealedKey}</code>
        </div>
      ) : null}

      <fieldset className="space-y-4" disabled={!canWrite}>
        <label className="block text-sm">
          <span className="mb-1.5 block font-medium text-label">Kamera-Anbieter</span>
          <select
            className="eh-input"
            value={provider}
            onChange={(e) => setProvider(e.target.value as LprProviderKind)}
          >
            {LPR_PROVIDER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {selected ? <p className="mt-1 text-xs text-label-secondary">{selected.hint}</p> : null}
        </label>

        <div className="rounded-xl border border-separator p-4 text-sm">
          <p className="font-medium text-label">Inbound-URL</p>
          <code className="mt-1 block break-all text-xs text-label-secondary">{inboundPath}</code>
          <p className="mt-2 text-xs text-label-secondary">
            Header: <code>X-Tenant-Slug</code> und <code>X-Api-Key</code> (LPR-Key).
          </p>
        </div>

        <div className="space-y-2 rounded-xl border border-separator p-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="rounded border-separator text-accent focus:ring-accent"
              checked={autoOpenBarrier}
              onChange={(e) => setAutoOpenBarrier(e.target.checked)}
            />
            Bei Treffer Schranke öffnen
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="rounded border-separator text-accent focus:ring-accent"
              checked={matchCheckedIn}
              onChange={(e) => setMatchCheckedIn(e.target.checked)}
            />
            Eingecheckte Buchungen matchen
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="rounded border-separator text-accent focus:ring-accent"
              checked={matchConfirmed}
              onChange={(e) => setMatchConfirmed(e.target.checked)}
            />
            Bestätigte Buchungen (Anreisetag) matchen
          </label>
        </div>
      </fieldset>

      <div className="flex flex-wrap gap-3">
        <Button type="button" disabled={!canWrite || saving} onClick={() => void save()}>
          {saving ? "Speichern…" : "Speichern"}
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={!canWrite || saving}
          onClick={() => void save({ rotate: true })}
        >
          {apiKeyConfigured ? "API-Key rotieren" : "API-Key erzeugen"}
        </Button>
        {apiKeyConfigured ? (
          <Button
            type="button"
            variant="secondary"
            disabled={!canWrite || saving}
            onClick={() => void save({ clear: true })}
          >
            API-Key löschen
          </Button>
        ) : null}
      </div>
    </div>
  );
}
